import React, { useState, useEffect } from 'react';
import { Download, AlertCircle, ArrowLeft } from 'lucide-react';
import { buildApiUrl } from '../../utils/apiSetup';

const PensionPayslipDetail = ({ runId, employeeId, onBack }) => {
  const [payslip, setPayslip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPayslip();
  }, [runId, employeeId]);

  const fetchPayslip = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/runs/${runId}/records/${employeeId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPayslip(data);
      } else {
        setError('Failed to fetch payslip details.');
      }
    } catch (err) {
      console.error(err);
      setError('Network error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/pension-payroll/runs/${runId}/records/${employeeId}/export/pdf`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pension_payslip_${payslip?.employee_code || 'payslip'}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <div className="p-6 text-center text-gray-500">Loading payslip details...</div>;
  if (error) return <div className="p-6 text-center text-red-600 flex items-center justify-center gap-2"><AlertCircle className="w-5 h-5" />{error}</div>;
  if (!payslip) return null;

  const record = payslip.record;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-150">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Records
        </button>
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium text-xs"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">PENSION PAYSLIP</h2>
        <p className="text-sm text-gray-500 mt-1">Generated Pension Slip Details</p>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-xs text-gray-500">Employee Code</p>
          <p className="font-semibold text-gray-800">{payslip.employee_code}</p>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-xs text-gray-500">Pensioner Name</p>
          <p className="font-semibold text-gray-800">{payslip.employee_name}</p>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-xs text-gray-500">Designation</p>
          <p className="font-semibold text-gray-800">{payslip.designation}</p>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-xs text-gray-500">Retirement Date</p>
          <p className="font-semibold text-gray-800">{payslip.retirement_date}</p>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-xs text-gray-500">Date of Death</p>
          <p className="font-semibold text-gray-800">{payslip.date_of_death}</p>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-xs text-gray-500">Pensioner Type</p>
          <p className="font-semibold text-gray-800">{payslip.pensioner_type}</p>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
        <div className="bg-gray-50 px-4 py-2 font-semibold text-gray-700 border-b border-gray-200 text-sm">Pension Details</div>
        <div className="divide-y divide-gray-150 text-sm">
          <div className="flex justify-between px-4 py-3">
            <span className="text-gray-600">Basic Amnt</span>
            <span className="font-semibold text-gray-800">₹{parseFloat(record.basic_pension).toFixed(2)}</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-gray-600">Dear. Allow. (DA {record.da_percentage}%)</span>
            <span className="font-semibold text-gray-800">₹{parseFloat(record.da_amount).toFixed(2)}</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-gray-600">D.A. Arrear</span>
            <span className="font-semibold text-gray-800">₹{parseFloat(record.da_arrear || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-gray-600">Medical Allow.</span>
            <span className="font-semibold text-gray-800">₹{parseFloat(record.medical_allowance || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between px-4 py-3 bg-gray-100 font-semibold">
            <span className="text-gray-700">Gross Total</span>
            <span className="text-gray-800">₹{parseFloat(record.gross_pension).toFixed(2)}</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-gray-600">Comm. Amnt.</span>
            <span className="font-semibold text-red-600">- ₹{parseFloat(record.commutation_amount || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-gray-600">Recovery</span>
            <span className="font-semibold text-red-600">- ₹{parseFloat(record.recovery || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-gray-600">Income Tax</span>
            <span className="font-semibold text-red-600">- ₹{parseFloat(record.income_tax || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between px-4 py-3 bg-primary-50 text-primary-950 font-bold">
            <span>Total Disbursed</span>
            <span>₹{parseFloat(record.net_pension).toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden text-sm">
        <div className="bg-gray-50 px-4 py-2 font-semibold text-gray-700 border-b border-gray-200">Disbursement Details</div>
        <div className="p-4 space-y-2">
          <div className="flex justify-between"><span className="text-gray-500">Bank Name</span><span className="font-medium">{payslip.bank_name || '-'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Account Number</span><span className="font-medium">{payslip.bank_account_number || '-'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">IFSC Code</span><span className="font-medium">{payslip.bank_ifsc_code || '-'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Branch</span><span className="font-medium">{payslip.bank_branch || '-'}</span></div>
        </div>
      </div>
    </div>
  );
};

export default PensionPayslipDetail;
